import * as React from "react";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, MenuItem, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { AddRounded } from "@mui/icons-material";

import PageTitle from "../../../components/PageTitle";
import { ChoiceChips, DataTable, EmptyState, SearchField } from "../../../components/ui";
import { SimpleSnackbar } from "../../../components/snackbar";
import { MessageDialog } from "../../../components/message-dialog";
import { Patient } from "../../../services/patient.service";
import { PatientForm } from "./form/patient-form";
import { usePatients } from "./use-patients";
import { buildPatientColumns } from "./patient-columns";
import { PATIENT_SORT_OPTIONS } from "./patients-filters";
import PatientMobileCard from "./components/PatientMobileCard";

export const PatientsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const useCompactSort = useMediaQuery(theme.breakpoints.down("lg"));
  const navigate = useNavigate();
  const openDetail = useCallback((id: number) => navigate(`/dashboard/patients/${id}`), [navigate]);

  const {
    loading,
    query,
    setQuery,
    page,
    setPage,
    pageSize,
    setPageSize,
    mobileVisible,
    setMobileVisible,
    mobilePageSize,
    sortOrder,
    setSortOrder,
    allPatients,
    allTags,
    filtered,
    sorted,
    pageRows,
    mobileSlice,
    openForm,
    selectedPatient,
    openDialog,
    showSnackbar,
    snackbarMessage,
    setShowSnackbar,
    handleSavePatient,
    handleDeletePatient,
    handleDelete,
    handleAddNew,
    closeForm,
    closeDeleteDialog,
  } = usePatients();

  const columns = useMemo(
    () => buildPatientColumns({ theme, sortOrder, onView: openDetail, onDelete: handleDelete }),
    [theme, sortOrder, openDetail, handleDelete]
  );

  const firstPatientEmpty = (
    <EmptyState
      title="Cadastre seu primeiro paciente"
      description="Organize dados, anamnese, fotos e histórico clínico em um só lugar."
      actionLabel="Novo paciente"
      onAction={handleAddNew}
    />
  );
  const noResultEmpty = (
    <EmptyState title="Nenhum resultado" description="Tente outro nome ou tag." actionLabel="Limpar busca" onAction={() => setQuery("")} />
  );

  return (
    <>
      <PageTitle title="Pacientes" />

      <Box sx={{ p: { xs: "18px 16px 32px", md: 4 }, width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            mb: 3,
          }}
        >
          <Typography sx={{ fontFamily: theme.typography.fontFamilyDisplay, fontSize: { xs: 18, md: 22 }, color: "text.primary" }}>
            <strong>{sorted.length}</strong> {query ? "pacientes encontrados" : "pacientes cadastrados"}
          </Typography>
          <Button variant="contained" startIcon={<AddRounded />} onClick={handleAddNew} sx={{ height: 46, px: 2.5, flexShrink: 0 }}>
            Novo paciente
          </Button>
        </Box>

        <SearchField value={query} onChange={setQuery} placeholder="Buscar por nome ou tag..." sx={{ mb: 3 }} />

        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", lg: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", lg: "row" },
            gap: 1.25,
            p: { xs: 1.5, md: 1.75 },
            mb: 2,
            borderRadius: "20px",
            border: (t) => `1px solid ${t.palette.divider}`,
            background: (t) => `linear-gradient(180deg, ${t.palette.background.paper} 0%, ${t.palette.background.default} 100%)`,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "text.secondary", mb: 0.5 }}>
              Ordenação
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "text.primary" }}>
              Organize sua lista de pacientes do jeito que preferir.
            </Typography>
          </Box>

          {useCompactSort ? (
            <TextField
              select
              fullWidth
              size="small"
              label="Ordenar por"
              value={sortOrder}
              onChange={(event) => setSortOrder(event.target.value as typeof sortOrder)}
              sx={{ width: "100%" }}
            >
              {PATIENT_SORT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <Box sx={{ width: "100%", minWidth: 0 }}>
              <ChoiceChips
                options={PATIENT_SORT_OPTIONS}
                value={sortOrder}
                onChange={setSortOrder}
                sx={{
                  maxWidth: "100%",
                  overflowX: { lg: "visible" },
                  flexWrap: { lg: "wrap" },
                  justifyContent: { lg: "flex-end" },
                  pb: { lg: 0 },
                }}
              />
            </Box>
          )}
        </Box>

        {!isMobile && (
          <DataTable<Patient>
            columns={columns}
            rows={pageRows}
            rowKey={(row) => String(row.id)}
            onRowClick={(row) => openDetail(row.id)}
            loading={loading}
            page={page}
            pageSize={pageSize}
            totalRows={sorted.length}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(1);
            }}
            minWidth={840}
            emptyState={allPatients.length === 0 && !loading ? firstPatientEmpty : noResultEmpty}
          />
        )}

        {isMobile && !loading && allPatients.length === 0 && firstPatientEmpty}
        {isMobile && !loading && allPatients.length > 0 && sorted.length === 0 && noResultEmpty}

        {isMobile && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {mobileSlice.map((patient) => (
              <PatientMobileCard key={patient.id} patient={patient} onOpen={() => openDetail(patient.id)} />
            ))}

            {mobileVisible < sorted.length && (
              <Button variant="outlined" fullWidth onClick={() => setMobileVisible((v) => v + mobilePageSize)} sx={{ mt: 1 }}>
                Carregar mais
              </Button>
            )}
          </Box>
        )}
      </Box>

      <PatientForm open={openForm} patient={selectedPatient} allTags={allTags} allPatients={allPatients} onClose={closeForm} onSave={handleSavePatient} isLoading={loading} />

      <MessageDialog
        open={openDialog}
        message={
          <>
            Você deseja remover <strong>TODAS</strong> as informações do paciente, incluindo dados pessoais, anamnese, fotos e histórico de eventos? Esta ação não pode ser desfeita.
          </>
        }
        onConfirm={() => handleDeletePatient()}
        confirmButtonText="Sim"
        onClose={closeDeleteDialog}
        cancelButtonText="Cancelar"
      />

      <SimpleSnackbar show={showSnackbar} message={snackbarMessage} onDismiss={() => setShowSnackbar(false)} />
    </>
  );
};
